// file: src/pages/AccessLogs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAccessLogs } from "../api/adminApi";
import Loader from "../components/Loader";

const BLUE = "#2A7EFA";
const clsx = (...p) => p.filter(Boolean).join(" ");

/* ---------- helpers ---------- */
function parseTimestampMs(ts) {
  if (ts == null) return null;
  if (typeof ts === "number") return ts < 1e12 ? ts * 1000 : ts;

  const s = String(ts);

  // If string already has TZ (Z or ±HH:MM), use as-is
  if (/[zZ]$/.test(s) || /[-+]\d{2}:\d{2}$/.test(s)) {
    const n = Date.parse(s);
    return Number.isNaN(n) ? null : n;
  }

  // If ISO without TZ → treat as UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(?:\.\d{1,6})?)?$/.test(s)) {
    const n = Date.parse(s + "Z");
    return Number.isNaN(n) ? null : n;
  }

  // Fallback
  const n = Date.parse(s);
  return Number.isNaN(n) ? null : n;
}

/* ---------- UI atoms ---------- */
function Pill({ tone = "slate", children }) {
  const map = {
    slate: ["bg-slate-50", "text-slate-700", "ring-slate-200"],
    emerald: ["bg-emerald-50", "text-emerald-700", "ring-emerald-100"],
    indigo: ["bg-indigo-50", "text-indigo-700", "ring-indigo-100"],
    violet: ["bg-violet-50", "text-violet-700", "ring-indigo-100"],
  }[tone];
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1",
        ...map
      )}
    >
      {children}
    </span>
  );
}

function ActionPill({ action }) {
  const tone =
    action === "LOGIN"
      ? "emerald"
      : action === "PLATE_LOOKUP"
      ? "indigo"
      : "slate";
  return (
    <span className="whitespace-nowrap">
      <Pill tone={tone}>{action || "—"}</Pill>
    </span>
  );
}

function PortalPill({ type }) {
  const label = type || "—";
  const tone =
    label === "ADMIN_PORTAL"
      ? "violet"
      : label === "LANDLORD_PORTAL"
      ? "indigo"
      : label === "TOWING_PORTAL"
      ? "emerald"
      : "slate";
  return (
    <span className="whitespace-nowrap">
      <Pill tone={tone}>{label.replace(/_/g, " ")}</Pill>
    </span>
  );
}

/** collapsible long text */
function Clamp({ text, max = 120 }) {
  const [open, setOpen] = useState(false);
  if (!text) return <span className="text-slate-500">—</span>;
  const s = String(text);
  const needs = s.length > max;
  const view = needs && !open ? s.slice(0, max) + "…" : s;
  return (
    <span className="inline">
      <span title={s}>{view}</span>
      {needs && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="ml-1 text-[11px] font-medium text-slate-600 hover:text-slate-800"
        >
          {open ? "less" : "more"}
        </button>
      )}
    </span>
  );
}

function EmailCell({ email }) {
  if (!email) return <span className="text-slate-500">—</span>;
  return (
    <div className="max-w-[260px] md:max-w-[320px] lg:max-w-[380px]">
      <span className="block truncate font-medium text-slate-900" title={email}>
        {email}
      </span>
    </div>
  );
}
const CENTRAL_TZ = "America/Chicago";
const fmtCST = (ms) =>
  ms ? new Date(ms).toLocaleString(undefined, { timeZone: CENTRAL_TZ }) : "—";

/* ---------- Mobile Card ---------- */
function Card({ row }) {
  return (
    <article className="rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm hover:shadow transition">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <ActionPill action={row.action} />
          <PortalPill type={row.resource_type} />
        </div>
        <div className="text-xs text-slate-500">
          {row.timestampMs ? fmtCST(row.timestampMs) : "—"}
        </div>
      </div>

      <div className="mt-2 text-sm">
        <div className="font-medium truncate" title={row.user_email || "—"}>
          {row.user_email || "—"}
        </div>
        <div className="text-slate-600">
          {row.user_role ? row.user_role.toUpperCase() : "—"}
          {row.company_name ? ` • ${row.company_name}` : ""}
        </div>
      </div>

      <div className="mt-3 grid gap-2 text-[13px]">
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">
            Details
          </div>
          <div className="mt-0.5 text-slate-700">
            {typeof row.details === "object" ? (
              <Clamp text={JSON.stringify(row.details)} />
            ) : (
              <Clamp text={row.details} />
            )}
          </div>
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3">
          <div className="grid gap-1">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Resource
            </div>
            <div className="text-slate-800">
              {row.resource_id ? (
                <span className="font-mono">{row.resource_id}</span>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="mt-3 grid gap-1">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              IP / Agent
            </div>
            <div className="text-slate-800">
              {row.ip_address || "—"} <span className="text-slate-500">•</span>{" "}
              <Clamp max={60} text={row.user_agent || "—"} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function Pager({ page, pageCount, onPrev, onNext }) {
  if (pageCount <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between">
      <div className="text-sm text-black/60">
        Page <span className="font-medium text-black">{page}</span> of{" "}
        <span className="font-medium text-black">{pageCount}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          className="rounded-lg border border-black/[0.06] px-3 py-2 text-sm disabled:opacity-50"
          disabled={page === 1}
        >
          Prev
        </button>
        <button
          onClick={onNext}
          className="rounded-lg border border-black/[0.06] px-3 py-2 text-sm disabled:opacity-50"
          disabled={page === pageCount}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function AccessLogs() {
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [since, setSince] = useState(""); // UI-only
  const [page, setPage] = useState(1);

  const UI_PAGE_SIZE = 15;
  const API_LIMIT = 100;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chunkRows, setChunkRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [apiPage, setApiPage] = useState(1);

  const derived = useMemo(() => {
    const uiIndexStart = (page - 1) * UI_PAGE_SIZE;
    const desiredApiPage = Math.floor(uiIndexStart / API_LIMIT) + 1;
    const apiSkip = (desiredApiPage - 1) * API_LIMIT;
    const localStart = uiIndexStart - apiSkip;
    const localEnd = localStart + UI_PAGE_SIZE;
    return { desiredApiPage, localStart, localEnd };
  }, [page]);

  // ⬆️ Near the top of AccessLogs.jsx, under helpers:

  const params = useMemo(
    () => ({
      skip: (derived.desiredApiPage - 1) * API_LIMIT,
      limit: API_LIMIT,
      action: action || undefined,
      search: searchApplied || undefined,
    }),
    [derived.desiredApiPage, action, searchApplied]
  );

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        if (apiPage === derived.desiredApiPage && chunkRows.length > 0) return;
        setLoading(true);
        setError("");
        const res = await getAccessLogs(params, { signal: ac.signal });

        const items = Array.isArray(res?.logs) ? res.logs : [];
        const count = typeof res?.total === "number" ? res.total : items.length;

        const normalized = items
          .map((r) => {
            const tsMs = parseTimestampMs(r.timestamp);
            return {
              id: r.id ?? `${r.action || "LOG"}_${tsMs ?? Math.random()}`,
              timestampMs: tsMs,
              timestamp: r.timestamp ?? null,
              action: r.action || "",
              resource_type: r.resource_type || "",
              resource_id: r.resource_id || "",
              details: r.details ?? null,
              ip_address: r.ip_address || "",
              user_agent: r.user_agent || "",
              user_email: r.user?.email || "",
              user_role: r.user?.role || "",
              company_name: r.user?.company_name || "",
              user_uid: r.user?.uid || "",
            };
          })
          // SORT DESC by time; nulls last
          .sort((a, b) => {
            const aa = a.timestampMs ?? -Infinity;
            const bb = b.timestampMs ?? -Infinity;
            return bb - aa;
          });

        setChunkRows(normalized);
        setTotal(count);
        setApiPage(derived.desiredApiPage);
      } catch (e) {
        if (!ac.signal.aborted) setError(e?.message || "Failed to load logs.");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Filter by since (client-side) THEN slice 15 rows
  const pageRows = useMemo(() => {
    let list = chunkRows;

    if (since) {
      const d = new Date(since);
      d.setHours(0, 0, 0, 0);
      const t0 = d.getTime();
      list = list.filter((r) => (r.timestampMs ?? 0) >= t0);
      // keep sorted desc after filtering
      list = list.sort(
        (a, b) => (b.timestampMs ?? -Infinity) - (a.timestampMs ?? -Infinity)
      );
    }

    return list.slice(
      Math.max(0, derived.localStart),
      Math.max(0, derived.localEnd)
    );
  }, [chunkRows, derived.localStart, derived.localEnd, since]);

  const pageCount = Math.max(1, Math.ceil(total / UI_PAGE_SIZE));
  const resetAndSearch = () => setPage(1);

  const applySearch = () => {
    setApiPage(0);
    setSearchApplied(search.trim());
    resetAndSearch();
  };

  const clearSearch = () => {
    setSearch("");
    setSearchApplied("");
    setApiPage(0);
    resetAndSearch();
  };

  return (
    <div className="space-y-6 antialiased text-slate-900 w-full max-w-full min-w-0 overflow-x-hidden">
      {/* Header / Filters */}
      <section className="rounded-3xl border border-black/[0.06] bg-white p-5 shadow-sm w-full max-w-full min-w-0">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between min-w-0">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,200px)_minmax(0,1fr)_auto] min-w-0 flex-1">
            <div>
              <label className="block text-sm font-medium">Action</label>
              <select
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  setApiPage(0);
                  resetAndSearch();
                }}
                className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4 focus:ring-[#2A7EFA]/15"
              >
                <option value="">All</option>
                <option value="LOGIN">LOGIN</option>
                <option value="PLATE_LOOKUP">PLATE_LOOKUP</option>
              </select>
            </div>

            <div className="min-w-0">
              <label className="block text-sm font-medium">Search</label>
              <div className="mt-1 flex gap-2 min-w-0">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applySearch();
                  }}
                  placeholder="Email, plate, or facility…"
                  className="min-w-0 flex-1 rounded-xl border border-black/[0.06] bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-[#2A7EFA]/15"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={applySearch}
                  className="shrink-0 rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${BLUE}, #5AA9FF)`,
                  }}
                >
                  Search
                </button>
                {searchApplied ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="shrink-0 rounded-xl border border-black/[0.06] bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500">
                Towing company email, license plate, or facility name
              </p>
            </div>
          </div>

          <div className="flex items-end gap-3 min-w-0">
            <div className="text-sm text-slate-600">
              Showing{" "}
              <span className="font-medium text-slate-900">
                {pageRows.length}
              </span>{" "}
              of <span className="font-medium text-slate-900">{total}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      {loading ? (
        <div className="grid place-items-center rounded-3xl border border-black/[0.06] bg-white p-12 min-h-[220px] w-full max-w-full min-w-0">
          <Loader size="lg" label="Loading access logs…" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : pageRows.length === 0 ? (
        <div className="grid place-items-center rounded-3xl border border-black/[0.06] bg-white p-12 text-slate-600">
          No access logs found.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <section className="hidden md:block rounded-3xl border border-black/[0.06] bg-white shadow-sm overflow-hidden">
            <div className="relative">
              <div
                className="overflow-x-auto"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <table className="min-w-[1120px] w-full text-left">
                  <colgroup>
                    <col style={{ width: 190 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 170 }} />
                    <col />
                    <col style={{ width: 220 }} />
                    <col style={{ width: 220 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 320 }} />
                    <col style={{ width: 320 }} />
                  </colgroup>
                  <thead className="bg-slate-50/80 backdrop-blur border-b border-slate-100 sticky top-0 z-10">
                    <tr className="text-[11px] uppercase tracking-wide text-slate-600">
                      <th className="px-6 py-3">Time</th>
                      <th className="px-6 py-3">Action</th>
                      <th className="px-6 py-3">Portal</th>
                      <th className="px-6 py-3">User</th>
                      <th className="px-6 py-3">Role / Company</th>
                      <th className="px-6 py-3">Resource</th>
                      <th className="px-6 py-3">User Agent</th>
                      <th className="px-6 py-3">Details</th>
                    </tr>
                  </thead>
                  <tbody className="text-[13.5px]">
                    {pageRows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={clsx(
                          "border-b border-slate-100 transition",
                          idx % 2 ? "bg-white" : "bg-slate-50/[0.25]"
                        )}
                      >
                        <td className="px-6 py-3 whitespace-nowrap text-slate-700">
                          {r.timestampMs ? fmtCST(r.timestampMs) : "—"}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <ActionPill action={r.action} />
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <PortalPill type={r.resource_type} />
                        </td>
                        <td className="px-6 py-3">
                          <EmailCell email={r.user_email} />
                        </td>
                        <td className="px-6 py-3">
                          <span className="block max-w-[220px] truncate">
                            {r.user_role ? r.user_role.toUpperCase() : "—"}
                            {r.company_name ? ` • ${r.company_name}` : ""}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          {r.resource_id ? (
                            <span
                              className="font-mono truncate block max-w-[220px]"
                              title={r.resource_id}
                            >
                              {r.resource_id}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td className="px-6 py-3">
                          <div
                            className="text-sm text-slate-700 truncate max-w-[320px]"
                            title={r.user_agent}
                          >
                            {r.user_agent || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <div
                            className="text-sm text-slate-700 truncate max-w-[320px]"
                            title={
                              typeof r.details === "object"
                                ? JSON.stringify(r.details)
                                : String(r.details || "—")
                            }
                          >
                            {r.details
                              ? typeof r.details === "object"
                                ? JSON.stringify(r.details)
                                : String(r.details)
                              : "—"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Fade edges */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 w-6"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,0))",
                }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 w-6"
                style={{
                  background:
                    "linear-gradient(270deg, rgba(255,255,255,1), rgba(255,255,255,0))",
                }}
              />
            </div>

            <div className="px-4 pb-4 pt-2">
              <Pager
                page={page}
                pageCount={pageCount}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
              />
            </div>
          </section>

          {/* Mobile cards */}
          <section className="md:hidden">
            <div className="grid gap-3">
              {pageRows.map((r) => (
                <Card key={r.id} row={r} />
              ))}
            </div>
            <Pager
              page={page}
              pageCount={pageCount}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
            />
          </section>
        </>
      )}
    </div>
  );
}
